import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Select,
  Spin,
  Tabs,
  message
} from "antd";
import { fetchSprTovList } from "../tov/spr-tov-api";
import {
  createSprNumberPriceUsl,
  fetchSprNumberPriceUsl,
  saveSprNumberPriceUsl
} from "./spr-number-price-api";
import SprNumberPricePrim from "./spr-number-price-prim";
import {
  IS_BABY_OPTIONS,
  IS_DAY_OPTIONS,
  VID_RSH_OPTIONS,
  normalizeUsl,
  pick,
  toNumber
} from "./spr-number-price-utils";

const MAIN_TAB = "main";
const PRIM_TAB = "prim";
const LABEL_WIDTH = 180;

const CARD_TABS = [
  { key: MAIN_TAB, label: "Основное" },
  { key: PRIM_TAB, label: "Применимость" }
];

const formItemLayout = {
  labelCol: { flex: `0 0 ${LABEL_WIDTH}px` },
  wrapperCol: { flex: "1 1 auto" },
  labelAlign: "right"
};

/**
 * Форма строки прайса. Delphi: UESprNumberPrice.
 */
const SprNumberPriceUslCard = ({
  idNumberPrice,
  idStr,
  isNew = false,
  open,
  onSaved,
  onCancel,
  onPrimChanged
}) => {
  const [form] = Form.useForm();
  const primRef = useRef(null);
  const [tab, setTab] = useState(MAIN_TAB);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [tovOptions, setTovOptions] = useState([]);

  useEffect(() => {
    if (!open) return undefined;
    setTab(MAIN_TAB);
    return undefined;
  }, [open, isNew, idStr]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    fetchSprTovList()
      .then((data) => {
        if (cancelled) return;
        const opts = (Array.isArray(data) ? data : [])
          .map((row) => {
            const idTov = toNumber(pick(row, "idTov", "IdTov"));
            const childCount = toNumber(pick(row, "childCount", "ChildCount")) ?? 0;
            const name = pick(row, "name", "Name") ?? "";
            if (idTov == null || childCount > 0) return null;
            return { value: idTov, label: name || `#${idTov}` };
          })
          .filter(Boolean)
          .sort((a, b) => a.label.localeCompare(b.label, "ru"));
        setTovOptions(opts);
      })
      .catch(() => {
        if (!cancelled) setTovOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;

    if (isNew) {
      setLoading(false);
      setError("");
      form.setFieldsValue({
        idx: 0,
        vidRsh: 0,
        name: "",
        idUsl: undefined,
        srokMin: 1,
        srokMax: 999,
        cntClMin: 1,
        cntClMax: 999,
        zena: 0,
        zenaHappy: 0,
        isDay: 0,
        isBaby: 0
      });
      return () => {
        cancelled = true;
      };
    }

    if (idStr == null) return undefined;

    setLoading(true);
    setError("");
    fetchSprNumberPriceUsl(idNumberPrice, idStr)
      .then((row) => {
        if (cancelled) return;
        const card = normalizeUsl(row);
        form.setFieldsValue({
          idStr: card.idStr,
          idx: card.idx,
          vidRsh: card.vidRsh,
          name: card.name,
          idUsl: card.idUsl > 0 ? card.idUsl : undefined,
          srokMin: card.srokMin,
          srokMax: card.srokMax,
          cntClMin: card.cntClMin,
          cntClMax: card.cntClMax,
          zena: card.zena,
          zenaHappy: card.zenaHappy,
          isDay: card.isDay,
          isBaby: card.isBaby
        });
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Не удалось загрузить строку");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, isNew, idNumberPrice, idStr, form]);

  const options = useMemo(() => {
    const idUsl = form.getFieldValue("idUsl");
    if (idUsl != null && !tovOptions.some((o) => o.value === idUsl)) {
      return [{ value: idUsl, label: `#${idUsl}` }, ...tovOptions];
    }
    return tovOptions;
  }, [tovOptions, form]);

  const onFinish = async (values) => {
    setSaving(true);
    setError("");
    const body = {
      idx: values.idx ?? 0,
      vidRsh: values.vidRsh,
      name: (values.name || "").trim(),
      idUsl: values.idUsl,
      vidMesta: 0,
      srokMin: values.srokMin ?? 1,
      srokMax: values.srokMax ?? 999,
      cntClMin: values.cntClMin ?? 1,
      cntClMax: values.cntClMax ?? 999,
      vidPutevki: 0,
      zena: values.zena ?? 0,
      zenaHappy: values.zenaHappy ?? 0,
      isDay: values.isDay,
      isBaby: values.isBaby
    };

    try {
      const saved = isNew
        ? await createSprNumberPriceUsl(idNumberPrice, body)
        : await saveSprNumberPriceUsl(idNumberPrice, { ...body, idStr });
      const savedId = toNumber(saved?.idStr ?? saved?.IdStr) ?? idStr;
      try {
        await primRef.current?.saveTo?.(savedId);
      } catch (primErr) {
        message.error(primErr.message || "Строка сохранена, применимость не удалось записать");
        onSaved?.(saved);
        return;
      }
      message.success("Сохранено");
      onSaved?.(saved);
    } catch (e) {
      setError(e.message || "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  const onTabChange = (key) => setTab(key);

  const isMain = tab === MAIN_TAB;

  if (loading) {
    return (
      <div className="spr-number-price-card__center">
        <Spin tip="Загрузка..." />
      </div>
    );
  }

  return (
    <div className="spr-number-price-card">
      <Tabs
        size="small"
        tabBarGutter={12}
        className="spr-number-price-card__tabs"
        activeKey={tab}
        onChange={onTabChange}
        items={CARD_TABS}
      />
      <div className="spr-number-price-card__body">
        {error ? (
          <Alert className="spr-number-price-card__error" type="error" showIcon message={error} />
        ) : null}
        <div hidden={!isMain} className="spr-number-price-card__main-pane">
          <Form
            form={form}
            className="spr-number-price-card__form"
            {...formItemLayout}
            colon={false}
            onFinish={onFinish}
            disabled={saving}
          >
            {!isNew ? (
              <Form.Item label="#" name="idStr">
                <Input disabled />
              </Form.Item>
            ) : null}
            <Form.Item label="Приоритет" name="idx">
              <InputNumber style={{ width: "100%" }} precision={0} />
            </Form.Item>
            <Form.Item
              label="Вид расчета"
              name="vidRsh"
              rules={[{ required: true, message: "Укажите вид расчета" }]}
            >
              <Select options={VID_RSH_OPTIONS} />
            </Form.Item>
            <Form.Item label="Наименование" name="name">
              <Input maxLength={80} />
            </Form.Item>
            <Form.Item
              label="Услуга"
              name="idUsl"
              rules={[{ required: true, message: "Укажите услугу" }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={options}
                placeholder="Выберите услугу"
              />
            </Form.Item>
            <Form.Item label="Срок MIN" name="srokMin">
              <InputNumber style={{ width: "100%" }} precision={0} min={0} />
            </Form.Item>
            <Form.Item label="Срок MAX" name="srokMax">
              <InputNumber style={{ width: "100%" }} precision={0} min={0} />
            </Form.Item>
            <Form.Item label="Клиентов MIN" name="cntClMin">
              <InputNumber style={{ width: "100%" }} precision={0} min={0} />
            </Form.Item>
            <Form.Item label="Клиентов MAX" name="cntClMax">
              <InputNumber style={{ width: "100%" }} precision={0} min={0} />
            </Form.Item>
            <Form.Item label="Цена будни" name="zena">
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
            <Form.Item label="Цена вых." name="zenaHappy">
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
            <Form.Item
              label="База расчета"
              name="isDay"
              rules={[{ required: true, message: "Укажите базу расчета" }]}
            >
              <Select options={IS_DAY_OPTIONS} />
            </Form.Item>
            <Form.Item
              label="Ребенок"
              name="isBaby"
              rules={[{ required: true, message: "Укажите признак" }]}
            >
              <Select options={IS_BABY_OPTIONS} />
            </Form.Item>
          </Form>
        </div>
        <div hidden={isMain} className="spr-number-price-card__prim-pane">
          <SprNumberPricePrim
            ref={primRef}
            embedded
            disabled={saving}
            idNumberPrice={idNumberPrice}
            idStr={isNew ? null : idStr}
            onChanged={onPrimChanged}
          />
        </div>
      </div>
      {isMain ? (
        <div className="spr-number-price-card__footer">
          <Button
            type="primary"
            loading={saving}
            onClick={() => form.submit()}
            className="spr-number-price-card__ok"
          >
            ОК
          </Button>
          <Button onClick={onCancel} disabled={saving}>
            Отмена
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default SprNumberPriceUslCard;
